<template>
    <div class="credit-recovery">
      <div class="header-section">
        <h2 class="page-title">Recupero Crediti</h2>
        <p class="subtitle">Gestione e monitoraggio delle pratiche di recupero crediti</p>
      </div>
      
      <div class="customer-section">
        <div class="section-header">
          <h3>Dati Cliente</h3>
        </div>
        
        <div class="customer-details">
          <div class="customer-info">
            <div class="data-row">
              <span class="label">Ragione Sociale:</span>
              <span class="value">Industrie Meccaniche S.r.l.</span>
            </div>
            <div class="data-row">
              <span class="label">Partita IVA:</span>
              <span class="value">IT98765432109</span>
            </div>
            <div class="data-row">
              <span class="label">Indirizzo:</span>
              <span class="value">Via dell'Industria 45, 70126 Bari (BA)</span>
            </div>
            <div class="data-row">
              <span class="label">Email:</span>
              <span class="value">amministrazione@industriemeccaniche.it</span>
            </div>
          </div>
          
          <div class="customer-contact">
            <div class="contact-info">
              <div class="data-row">
                <span class="label">Contatto:</span>
                <span class="value">Giorgio Bianchi</span>
              </div>
              <div class="data-row">
                <span class="label">Telefono:</span>
                <span class="value">+39 080 555 7890</span>
              </div>
              <div class="data-row">
                <span class="label">Stato Credito:</span>
                <span class="value status-critical">Critico (> 90 giorni)</span>
              </div>
            </div>
            
            <div class="contact-actions">
              <Button icon="pi pi-phone" label="Chiama cliente" class="p-button-outlined" @click="chiamaCliente" />
              <Button icon="pi pi-envelope" label="Invia sollecito" class="p-button-outlined" @click="inviaSollecito" />
            </div>
          </div>
        </div>
      </div>
      
      <div class="statement-section">
        <div class="section-header">
          <h3>Estratto Conto</h3>
          <div class="section-controls">
            <Button icon="pi pi-download" class="p-button-text" v-tooltip.left="'Scarica estratto conto'" @click="scaricaEstrattoQuery" />
            <Button icon="pi pi-print" class="p-button-text" v-tooltip.left="'Stampa'" @click="stampaEstrattoQuery" />
          </div>
        </div>
        
        <div class="statement-filters">
          <div class="filter-controls">
            <span class="filter-label">Periodo:</span>
            <Dropdown v-model="periodoSelezionato" :options="periodi" optionLabel="nome" class="p-inputtext-sm" />
            
            <span class="filter-label ml-3">Visualizza:</span>
            <MultiSelect v-model="tipiMovimentiSelezionati" :options="tipiMovimenti" optionLabel="nome" placeholder="Tutti i movimenti" class="p-inputtext-sm" />
            
            <Button icon="pi pi-filter" label="Applica filtri" class="p-button-sm ml-3" @click="applicaFiltri" />
          </div>
          
          <div class="summary-info">
            <div class="summary-item">
              <span class="summary-label">Totale Fatturato:</span>
              <span class="summary-value">€ 42.850,00</span>
            </div>
            <div class="summary-item">
              <span class="summary-label">Totale Pagato:</span>
              <span class="summary-value">€ 26.720,00</span>
            </div>
            <div class="summary-item">
              <span class="summary-label">Saldo:</span>
              <span class="summary-value negative-amount">€ 16.130,00</span>
            </div>
          </div>
        </div>
        
        <div class="statement-table">
          <DataTable :value="movimenti" responsiveLayout="scroll" stripedRows>
            <Column field="data" header="Data" :sortable="true">
              <template #body="{data}">
                {{ formatDate(data.data) }}
              </template>
            </Column>
            <Column field="documento" header="Documento" :sortable="true"></Column>
            <Column field="descrizione" header="Descrizione"></Column>
            <Column field="scadenza" header="Scadenza">
              <template #body="{data}">
                {{ formatDate(data.scadenza) }}
              </template>
            </Column>
            <Column field="importo" header="Importo" :sortable="true">
              <template #body="{data}">
                <span :class="{'negative-amount': data.importo < 0, 'positive-amount': data.importo > 0}">
                  € {{ formatAmount(data.importo) }}
                </span>
              </template>
            </Column>
            <Column field="stato" header="Stato">
              <template #body="{data}">
                <span :class="'status-badge status-' + data.stato.toLowerCase().replace(' ', '-')">
                  {{ data.stato }}
                </span>
              </template>
            </Column>
            <Column header="Azioni">
              <template #body="{data}">
                <Button icon="pi pi-eye" class="p-button-rounded p-button-text p-button-sm" v-tooltip.top="'Visualizza dettagli'" @click="visualizzaDettagli(data)" />
                <Button icon="pi pi-file" class="p-button-rounded p-button-text p-button-sm" v-tooltip.top="'Visualizza documento'" @click="visualizzaDocumento(data)" />
              </template>
            </Column>
          </DataTable>
        </div>
      </div>
      
      <div class="action-section">
        <div class="section-header">
          <h3>Gestione Recupero</h3>
        </div>
        
        <div class="action-details">
          <div class="action-history">
            <h4>Storico Azioni</h4>
            <Timeline :value="azioni" content="descrizione" opposite="data">
              <template #marker="slotProps">
                <span :class="'timeline-marker ' + slotProps.item.tipo">
                  <i :class="'pi ' + slotProps.item.icona"></i>
                </span>
              </template>
            </Timeline>
          </div>
          
          <div class="action-container">
            <div class="info-grid">
              <div class="info-box">
                <i class="pi pi-calendar-times info-icon"></i>
                <div class="info-content">
                  <span class="info-title">Ritardo medio pagamenti</span>
                  <span class="info-value">37 giorni</span>
                </div>
              </div>
              
              <div class="info-box">
                <i class="pi pi-percentage info-icon"></i>
                <div class="info-content">
                  <span class="info-title">Probabilità recupero</span>
                  <span class="info-value">68%</span>
                </div>
              </div>
              
              <div class="info-box">
                <i class="pi pi-exclamation-triangle info-icon"></i>
                <div class="info-content">
                  <span class="info-title">Livello rischio</span>
                  <span class="info-value status-warning">Medio-Alto</span>
                </div>
              </div>
            </div>
            
            <div class="notes-section">
              <h4>Note interne</h4>
              <div class="note-container">
                <div class="note-item">
                  <div class="note-header">
                    <span class="note-author">Marco Verdi</span>
                    <span class="note-date">01/05/2025</span>
                  </div>
                  <div class="note-text">
                    Cliente contattato telefonicamente. Ha promesso pagamento entro il 10/05. Da ricontattare se non si riceve il bonifico.
                  </div>
                </div>
              </div>
              
              <div class="note-form">
                <Textarea v-model="nuovaNota" rows="2" placeholder="Aggiungi una nota..." class="w-full" />
                <div class="note-form-actions">
                  <Button label="Aggiungi nota" icon="pi pi-plus" class="p-button-sm" @click="aggiungiNota" />
                </div>
              </div>
            </div>
            
            <div class="action-buttons">
              <Button label="Invia diffida legale" icon="pi pi-envelope" class="p-button-warning" @click="inviaDiffidaLegale" />
              <Button label="Avvia procedura recupero" icon="pi pi-check" class="p-button-success" @click="avviaProceduraRecupero" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  import { ref, reactive, onMounted, getCurrentInstance } from 'vue';
  import Service from '@/services/ask-pratiche/pratiche-wizard';
  import { useToast } from 'primevue/usetoast';
  
  export default {
    setup() {
      const toast = useToast();
      const loading = ref(false);
      const self = getCurrentInstance();
      self.$eventBus = self.appContext.config.globalProperties.$eventBus;
      const service = new Service(self.$eventBus);
      
      // Dati per i filtri
      const periodoSelezionato = ref({ nome: 'Ultimi 12 mesi', valore: '12m' });
      const periodi = ref([
        { nome: 'Ultimi 3 mesi', valore: '3m' },
        { nome: 'Ultimi 6 mesi', valore: '6m' },
        { nome: 'Ultimi 12 mesi', valore: '12m' },
        { nome: 'Anno corrente', valore: 'year' },
        { nome: 'Tutto', valore: 'all' }
      ]);
      
      const tipiMovimentiSelezionati = ref([]);
      const tipiMovimenti = ref([
        { nome: 'Fatture', valore: 'fattura' },
        { nome: 'Pagamenti', valore: 'pagamento' },
        { nome: 'Note credito', valore: 'nota_credito' },
        { nome: 'Solleciti', valore: 'sollecito' }
      ]);
      
      // Dati movimenti estratto conto
      const movimenti = ref([
        { 
          id: 1, 
          data: new Date(2024, 9, 15), 
          documento: 'FAT-2024-00987', 
          descrizione: 'Fornitura Q4 2024', 
          scadenza: new Date(2024, 10, 15), 
          importo: 8500.00, 
          stato: 'Pagata' 
        },
        { 
          id: 2, 
          data: new Date(2024, 10, 20), 
          documento: 'PAG-2024-00654', 
          descrizione: 'Pagamento FAT-2024-00987', 
          scadenza: null, 
          importo: -8500.00, 
          stato: 'Completato' 
        },
        { 
          id: 3, 
          data: new Date(2024, 11, 10), 
          documento: 'FAT-2024-01045', 
          descrizione: 'Fornitura aggiuntiva Q4 2024', 
          scadenza: new Date(2024, 12, 10), 
          importo: 5350.00, 
          stato: 'Pagata' 
        },
        { 
          id: 4, 
          data: new Date(2024, 12, 18), 
          documento: 'PAG-2024-00721', 
          descrizione: 'Pagamento FAT-2024-01045', 
          scadenza: null, 
          importo: -5350.00, 
          stato: 'Completato' 
        },
        { 
          id: 5, 
          data: new Date(2025, 0, 15), 
          documento: 'FAT-2025-00123', 
          descrizione: 'Fornitura Q1 2025', 
          scadenza: new Date(2025, 1, 15), 
          importo: 9250.00, 
          stato: 'Pagata parziale' 
        },
        { 
          id: 6, 
          data: new Date(2025, 1, 20), 
          documento: 'PAG-2025-00087', 
          descrizione: 'Acconto FAT-2025-00123', 
          scadenza: null, 
          importo: -5000.00, 
          stato: 'Completato' 
        },
        { 
          id: 7, 
          data: new Date(2025, 2, 5), 
          documento: 'FAT-2025-00347', 
          descrizione: 'Servizi di manutenzione straordinaria', 
          scadenza: new Date(2025, 3, 5), 
          importo: 3800.00, 
          stato: 'Pagata' 
        },
        { 
          id: 8, 
          data: new Date(2025, 3, 1), 
          documento: 'PAG-2025-00156', 
          descrizione: 'Pagamento FAT-2025-00347', 
          scadenza: null, 
          importo: -3800.00, 
          stato: 'Completato' 
        },
        { 
          id: 9, 
          data: new Date(2025, 2, 28), 
          documento: 'FAT-2025-00562', 
          descrizione: 'Fornitura Q2 2025', 
          scadenza: new Date(2025, 3, 28), 
          importo: 11750.00, 
          stato: 'Scaduta' 
        },
        { 
          id: 10, 
          data: new Date(2025, 3, 15), 
          documento: 'SOL-2025-00023', 
          descrizione: 'Primo sollecito FAT-2025-00562', 
          scadenza: new Date(2025, 3, 28), 
          importo: 0.00, 
          stato: 'Inviato' 
        },
        { 
          id: 11, 
          data: new Date(2025, 4, 1), 
          documento: 'FAT-2025-00789', 
          descrizione: 'Servizi di consulenza tecnica', 
          scadenza: new Date(2025, 5, 1), 
          importo: 4200.00, 
          stato: 'Emessa' 
        },
        { 
          id: 12, 
          data: new Date(2025, 4, 2), 
          documento: 'SOL-2025-00045', 
          descrizione: 'Secondo sollecito FAT-2025-00562', 
          scadenza: new Date(2025, 3, 28), 
          importo: 0.00, 
          stato: 'Inviato' 
        }
      ]);
      
      // Dati storico azioni
      const azioni = ref([
        {
          data: '03/05/2025',
          descrizione: 'Secondo sollecito telefonico effettuato',
          tipo: 'call',
          icona: 'pi-phone'
        },
        {
          data: '02/05/2025',
          descrizione: 'Secondo sollecito di pagamento inviato via PEC',
          tipo: 'email',
          icona: 'pi-envelope'
        },
        {
          data: '15/04/2025',
          descrizione: 'Primo sollecito telefonico effettuato',
          tipo: 'call',
          icona: 'pi-phone'
        },
        {
          data: '10/04/2025',
          descrizione: 'Primo sollecito di pagamento inviato via email',
          tipo: 'email',
          icona: 'pi-envelope'
        },
        {
          data: '30/03/2025',
          descrizione: 'Scadenza fattura FAT-2025-00562',
          tipo: 'warning',
          icona: 'pi-exclamation-circle'
        }
      ]);
      
      const nuovaNota = ref('');
      
      const aggiungiNota = () => {
        if (nuovaNota.value.trim() === '') {
          toast.add({
            severity: 'warn',
            summary: 'Nota vuota',
            detail: 'Inserisci un testo per la nota',
            life: 3000
          });
          return;
        }
        
        toast.add({
          severity: 'success',
          summary: 'Nota aggiunta',
          detail: 'La nota è stata aggiunta con successo',
          life: 3000
        });
        
        nuovaNota.value = '';
      };
      
      const formatDate = (date) => {
        if (!date) return '';
        if (typeof date === 'string') return date;
        
        return date.toLocaleDateString('it-IT', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });
      };
      
      const formatAmount = (amount) => {
        return Math.abs(amount).toFixed(2).replace('.', ',');
      };
      
      const chiamaCliente = () => {
        toast.add({
          severity: 'info',
          summary: 'Chiamata in corso',
          detail: 'Inizializzazione chiamata al numero +39 080 555 7890',
          life: 3000
        });
      };
      
      const inviaSollecito = () => {
        toast.add({
          severity: 'info',
          summary: 'Sollecito',
          detail: 'Preparazione email di sollecito in corso',
          life: 3000
        });
      };
      
      const inviaDiffidaLegale = () => {
        toast.add({
          severity: 'warning',
          summary: 'Diffida legale',
          detail: 'Inizializzazione procedura di diffida legale',
          life: 3000
        });
      };
      
      const avviaProceduraRecupero = () => {
        toast.add({
          severity: 'info',
          summary: 'Procedura di recupero',
          detail: 'Avvio procedura di recupero crediti con agenzia esterna',
          life: 3000
        });
      };
      
      const visualizzaDettagli = (data) => {
        toast.add({
          severity: 'info',
          summary: 'Dettagli movimento',
          detail: `Visualizzazione dettagli per ${data.documento}`,
          life: 3000
        });
      };
      
      const visualizzaDocumento = (data) => {
        toast.add({
          severity: 'info',
          summary: 'Visualizza documento',
          detail: `Apertura documento ${data.documento}`,
          life: 3000
        });
      };
      
      const scaricaEstrattoQuery = () => {
        toast.add({
          severity: 'info',
          summary: 'Download',
          detail: 'Download estratto conto in formato Excel',
          life: 3000
        });
      };
      
      const stampaEstrattoQuery = () => {
        toast.add({
          severity: 'info',
          summary: 'Stampa',
          detail: 'Preparazione documento per la stampa',
          life: 3000
        });
      };
      
      const applicaFiltri = () => {
        loading.value = true;
        
        setTimeout(() => {
          loading.value = false;
          toast.add({
            severity: 'success',
            summary: 'Filtri applicati',
            detail: 'I dati sono stati filtrati con successo',
            life: 3000
          });
        }, 500);
      };
  
      const caricaDati = async () => {
        loading.value = true;
        
        try {
          console.log('Caricamento dati recupero crediti');
        } catch (errore) {
          toast.add({
            severity: 'error',
            summary: 'Errore di caricamento',
            detail: 'Impossibile caricare i dati di recupero crediti',
            life: 3000
          });
        } finally {
          loading.value = false;
        }
      };
  
      onMounted(() => {
        caricaDati();
      });
  
      return {
        loading,
        movimenti,
        azioni,
        periodoSelezionato,
        periodi,
        tipiMovimentiSelezionati,
        tipiMovimenti,
        nuovaNota,
        formatDate,
        formatAmount,
        chiamaCliente,
        inviaSollecito,
        inviaDiffidaLegale,
        avviaProceduraRecupero,
        visualizzaDettagli,
        visualizzaDocumento,
        scaricaEstrattoQuery,
        stampaEstrattoQuery,
        applicaFiltri,
        aggiungiNota
      };
    }
  };
  </script>
  
  <style scoped>
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700&display=swap');
  
  .credit-recovery {
    font-family: 'Montserrat', sans-serif;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    margin: 0;
    padding: 1.5rem;
  }
  
  .header-section {
    margin-bottom: 1.5rem;
    border-bottom: 1px solid #e0e0e0;
    padding-bottom: 1rem;
  }
  
  .page-title {
    font-size: 1.8rem;
    font-weight: 600;
    color: #2c3e50;
    margin: 0;
  }
  
  .subtitle {
    color: #7f8c8d;
    font-size: 1rem;
    margin-top: 0.5rem;
  }
  
  .customer-section,
  .statement-section,
  .action-section {
    background-color: white;
    border-radius: 12px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    margin-bottom: 1.5rem;
    overflow: hidden;
  }
  
  .section-header {
    background-color: #f8f9fa;
    padding: 1rem 1.5rem;
    border-bottom: 1px solid #eaeaea;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  
  .section-header h3 {
    font-size: 1.2rem;
    font-weight: 600;
    color: #3498db;
    margin: 0;
  }
  
  .section-controls {
    display: flex;
    gap: 0.5rem;
  }
  
  .customer-details,
  .action-details {
    padding: 1.5rem;
  }
  
  .customer-details {
    display: flex;
    flex-wrap: wrap;
    gap: 2rem;
  }
  
  .customer-info {
    flex: 1;
    min-width: 300px;
  }
  
  .customer-contact {
    flex: 1;
    min-width: 300px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
  
  .contact-actions {
    display: flex;
    gap: 1rem;
    margin-top: 1rem;
  }
  
  .data-row {
    display: flex;
    margin-bottom: 0.8rem;
  }
  
  .label {
    flex: 0 0 150px;
    font-weight: 600;
    color: #7f8c8d;
  }
  
  .value {
    flex: 1;
    color: #2c3e50;
  }
  
  .statement-filters {
    padding: 1rem 1.5rem;
    background-color: #f8f9fa;
    border-bottom: 1px solid #eaeaea;
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 1rem;
  }
  
  .filter-controls {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.5rem;
  }
  
  .filter-label {
    font-weight: 600;
    color: #7f8c8d;
  }
  
  .summary-info {
    display: flex;
    gap: 1.5rem;
  }
  
  .summary-item {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
  }
  
  .summary-label {
    font-size: 0.8rem;
    color: #7f8c8d;
  }
  
  .summary-value {
    font-weight: 600;
    color: #2c3e50;
    font-size: 1.1rem;
  }
  
  .statement-table {
    padding: 1.5rem;
  }
  
  .negative-amount {
    color: #e74c3c;
    font-weight: 600;
  }
  
  .positive-amount {
    color: #2c3e50;
    font-weight: 600;
  }
  
  .status-badge {
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    font-size: 0.8rem;
    font-weight: 600;
  }
  
  .status-pagata {
    background-color: #dff0d8;
    color: #3c763d;
  }
  
  .status-emessa {
    background-color: #d9edf7;
    color: #31708f;
  }
  
  .status-scaduta {
    background-color: #fcf8e3;
    color: #8a6d3b;
  }
  
  .status-pagata-parziale {
    background-color: #e8f5e9;
    color: #2e7d32;
  }
  
  .status-completato {
    background-color: #dff0d8;
    color: #3c763d;
  }
  
  .status-inviato {
    background-color: #d9edf7;
    color: #31708f;
  }
  
  .status-critical {
    color: #e74c3c;
    font-weight: 600;
  }
  
  .status-warning {
    color: #f39c12;
    font-weight: 600;
  }
  
  .action-history {
    margin-bottom: 2rem;
  }
  
  .action-history h4 {
    font-size: 1.1rem;
    font-weight: 600;
    color: #2c3e50;
    margin-top: 0;
    margin-bottom: 1rem;
  }
  
  .timeline-marker {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    border-radius: 50%;
  }
  
  .timeline-marker.call {
    background-color: #3498db;
    color: white;
  }
  
  .timeline-marker.email {
    background-color: #2ecc71;
    color: white;
  }
  
  .timeline-marker.warning {
    background-color: #f39c12;
    color: white;
  }
  
  .info-grid {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 1rem;
    margin-bottom: 1.5rem;
  }
  
  .info-box {
    flex: 1;
    min-width: 180px;
    display: flex;
    align-items: center;
    background-color: #f8f9fa;
    padding: 1rem;
    border-radius: 8px;
  }
  
  .info-icon {
    font-size: 1.5rem;
    color: #3498db;
    margin-right: 1rem;
  }
  
  .info-content {
    display: flex;
    flex-direction: column;
  }
  
  .info-title {
    font-size: 0.8rem;
    color: #7f8c8d;
  }
  
  .info-value {
    font-weight: 600;
    color: #2c3e50;
  }
  
  .notes-section {
    background-color: #f8f9fa;
    border-radius: 8px;
    padding: 1rem;
    margin-bottom: 1.5rem;
  }
  
  .notes-section h4 {
    font-size: 1rem;
    font-weight: 600;
    color: #2c3e50;
    margin-top: 0;
    margin-bottom: 1rem;
  }
  
  .note-container {
    margin-bottom: 1rem;
  }
  
  .note-item {
    background-color: white;
    border-radius: 6px;
    padding: 0.8rem;
    margin-bottom: 0.8rem;
    border-left: 3px solid #3498db;
  }
  
  .note-header {
    display: flex;
    justify-content: space-between;
    margin-bottom: 0.5rem;
  }
  
  .note-author {
    font-weight: 600;
    color: #2c3e50;
  }
  
  .note-date {
    font-size: 0.8rem;
    color: #7f8c8d;
  }
  
  .note-text {
    font-size: 0.9rem;
    color: #2c3e50;
  }
  
  .note-form-actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 0.8rem;
  }
  
  .action-buttons {
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
    margin-top: 1.5rem;
  }
  
  .w-full {
    width: 100%;
  }
  
  .ml-3 {
    margin-left: 0.75rem;
  }
  
  @media (max-width: 768px) {
    .customer-details,
    .summary-info,
    .info-grid {
      flex-direction: column;
    }
    
    .contact-actions,
    .action-buttons {
      flex-direction: column;
      gap: 0.5rem;
    }
    
    .summary-item {
      align-items: flex-start;
    }
    
    .filter-controls {
      margin-bottom: 1rem;
    }
  }
  </style>